// Document references (section 6.4): the field shows the target as a card with Open / Change / Clear; the picker modal
// lists the target template and its heirs as cards (label, first three values, a thumbnail), with search, None and
// "+ Create" (the new draft is pushed onto the editor's stack with a breadcrumb and the picker closes; Done there commits
// the reference, Cancel commits nothing; section 6.2); drafts are listed by their temp id. The picker's search applies as you
// type after a 300 ms pause, Enter at once (section 6.6, ui/useDebounced.ts).
import { useAssetUrls } from '../../api/assetUrls';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ApiError } from '../../api/client';
import { ops } from '../../api/operations';
import type { Document, Json, Template } from '../../api/types';
import { Button, IconButton } from '../../ui/Button';
import { Badge } from '../../ui/Chip';
import { Combobox } from '../../ui/Combobox';
import { EmptyState } from '../../ui/Banner';
import { AlertCircle, ExternalLink, FileText, Link as LinkIcon, Plus, X } from '../../ui/icons';
import { Select, TextInput } from '../../ui/inputs';
import { Modal } from '../../ui/Modal';
import { useSearchBox } from '../../ui/useDebounced';
import { useWorkspaceVersion } from '../../workspace/react';
import { isTempId } from '../../workspace/Workspace';
import { formatValue, shortId, type DocumentLike } from '../format';
import { isScriptTemplate } from '../schema';
import { createDocumentDraft, targetsOf, useWidgetEnv, type WidgetProps } from './context';

export function ReferenceField(props: WidgetProps) {
  const { param: p, value, onCommit, disabled, compact, id } = props;
  const env = useWidgetEnv();
  const { catalog, host, client, org, game, branch, workspace } = env;
  useWorkspaceVersion(workspace);
  const [picking, setPicking] = useState(false);
  const current = typeof value === 'string' ? value : null;
  const target = current ? catalog.resolve(current) : undefined;
  const label = target ? catalog.label(target) : current ? (isTempId(current) ? 'new document' : current.slice(0, 8) + '…') : null;
  const script = isScriptTemplate(p.template);

  if (compact) {
    return <CompactReference {...props} />;
  }
  return (
    <span className="ref-field">
      {current ? (
        <span className="ref-card" data-testid={`ref-${p.name}`}>
          <FileText className="icon-s muted" aria-hidden="true" />
          <span className="ref-label">{label}</span>
          {target && <Badge>{target.template.split('.').pop()}</Badge>}
          {host && (
            <IconButton label={`Open ${label ?? 'document'}`} onClick={() => void host.open(current)}>
              <ExternalLink aria-hidden="true" />
            </IconButton>
          )}
        </span>
      ) : (
        <span className="ref-card muted">None</span>
      )}
      {!disabled && (
        <>
          <Button size="s" onClick={() => setPicking(true)} id={id} aria-label={`${current ? 'Change' : 'Pick'} ${p.displayName ?? p.name}`} icon={<LinkIcon aria-hidden="true" />}>
            {current ? 'Change…' : 'Pick…'}
          </Button>
          {current && (
            <Button size="s" variant="ghost" onClick={() => onCommit(null)} icon={<X aria-hidden="true" />}>
              Clear
            </Button>
          )}
        </>
      )}
      {script && <span className="muted small">Visual scripting is retired; reward tables replace dynamic rewards. Existing script documents can still be picked.</span>}
      {picking && (
        <ReferencePicker
          param={p}
          selected={current ? [current] : []}
          onClose={() => setPicking(false)}
          onPick={(ids) => {
            setPicking(false);
            onCommit(ids[0] ?? null);
          }}
          allowNone
        />
      )}
      <span hidden>{[client, org, game, branch].length}</span>
    </span>
  );
}

/** The grid cell: a searchable combobox over the target family (server search), None included. */
function CompactReference({ param: p, value, onCommit, disabled, id }: WidgetProps) {
  const { catalog, client, org, game, branch, workspace } = useWidgetEnv();
  const [found, setFound] = useState<Document[]>([]);
  const [loading, setLoading] = useState(false);
  const current = typeof value === 'string' ? value : null;
  const targets = useMemo(() => targetsOf(catalog, p), [catalog, p]);
  const search = useCallback(
    async (text: string) => {
      if (!p.template) return;
      setLoading(true);
      try {
        const page = await client.data(ops.listDocuments, { path: { org, game, branch }, query: { template: p.template, includeHeirs: 'true', search: text || undefined, limit: 20 } });
        setFound(page.items);
      } catch {
        setFound([]);
      } finally {
        setLoading(false);
      }
    },
    [client, org, game, branch, p.template],
  );
  useEffect(() => {
    void search('');
  }, [search]);
  const options = useMemo(() => {
    const staged = workspace.all.filter((d) => d.kind === 'document' && d.status === 'new' && targets.some((t) => t.name === d.template)).map((d) => d.current as unknown as DocumentLike);
    const all = [...staged, ...found.filter((f) => !staged.some((s) => s.id === f.id))];
    if (current && !all.some((d) => d.id === current)) {
      const own = catalog.resolve(current);
      if (own) all.unshift(own);
    }
    return all.map((d) => ({ value: d.id, label: catalog.label(d), hint: d.template.split('.').pop() ?? undefined }));
  }, [found, workspace, targets, current, catalog]);
  return <Combobox id={id} size="s" value={current} options={options} onChange={(v) => onCommit(v)} onSearch={(t) => void search(t)} loading={loading} emptyLabel="None" disabled={disabled} aria-label={p.displayName ?? p.name} />;
}

export interface ReferencePickerProps {
  param: Pick<WidgetProps['param'], 'template' | 'name' | 'displayName' | 'type'>;
  selected: string[];
  multi?: boolean;
  allowNone?: boolean;
  onPick: (ids: string[]) => void;
  onClose: () => void;
}

/** The reference picker modal (section 6.4): the target family's documents and drafts as cards; search; None; "+ Create". */
export function ReferencePicker({ param: p, selected, multi, allowNone, onPick, onClose }: ReferencePickerProps) {
  const { catalog, client, org, game, branch, workspace, host } = useWidgetEnv();
  useWorkspaceVersion(workspace);
  const targets = useMemo(() => targetsOf(catalog, p), [catalog, p]);
  const [template, setTemplate] = useState<string>(p.template ?? targets[0]?.name ?? '');
  const includeHeirs = template === (p.template ?? '');
  const [search, setSearch] = useState('');
  const searchBox = useSearchBox(search, setSearch);
  const [items, setItems] = useState<Document[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<Set<string>>(() => new Set(selected));
  const [createIn, setCreateIn] = useState<string>(targets.find((t) => t.name === (p.template ?? ''))?.name ?? targets[0]?.name ?? '');
  const requestId = useRef(0);

  const load = useCallback(
    async (after: string | null) => {
      if (!template) return;
      const mine = ++requestId.current;
      setLoading(true);
      try {
        const page = await client.data(ops.listDocuments, { path: { org, game, branch }, query: { template, includeHeirs: includeHeirs ? 'true' : undefined, search: search || undefined, limit: 50, cursor: after ?? undefined } });
        if (mine !== requestId.current) return;
        setItems((prev) => (after ? [...prev, ...page.items] : page.items));
        setCursor(page.nextCursor);
        setError(null);
      } catch (e) {
        if (mine === requestId.current) setError(e instanceof ApiError ? e.message : String(e));
      } finally {
        if (mine === requestId.current) setLoading(false);
      }
    },
    [client, org, game, branch, template, includeHeirs, search],
  );
  useEffect(() => {
    void load(null);
  }, [load]);

  const drafts = useMemo(() => {
    const family = new Set((includeHeirs ? targets : targets.filter((t) => t.name === template)).map((t) => t.name));
    return workspace.all.filter((d) => d.kind === 'document' && d.status === 'new' && family.has(d.template ?? '')).map((d) => d.current as unknown as DocumentLike);
  }, [workspace, targets, template, includeHeirs]); // eslint-disable-line react-hooks/exhaustive-deps
  const rows: DocumentLike[] = useMemo(() => {
    const deleted = new Set(workspace.all.filter((d) => d.kind === 'document' && d.status === 'deleted').map((d) => d.id));
    const overlaid = items.filter((d) => !deleted.has(d.id)).map((d) => (workspace.getDraft(`document:${d.id}`)?.current as unknown as DocumentLike | undefined) ?? d);
    const q = search.trim().toLowerCase();
    const staged = q ? drafts.filter((d) => catalog.label(d).toLowerCase().includes(q)) : drafts;
    return [...staged, ...overlaid];
  }, [items, drafts, workspace, search, catalog]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id: string) => {
    if (!multi) {
      onPick([id]);
      return;
    }
    setChosen((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const create = async () => {
    const t = catalog.templates.get(createIn);
    if (!t) return;
    const mark = workspace.mark();
    const tempId = createDocumentDraft(workspace, t);
    if (!host) {
      if (multi) setChosen((s) => new Set(s).add(tempId));
      else onPick([tempId]);
      return;
    }
    // section 6.4: the new draft is pushed onto the editor's stack (no dialog over a dialog) and the picker closes; Done
    // there commits the reference, Cancel reverts the draft (to the mark taken before it was created) and commits nothing
    const already = Array.from(chosen);
    onClose();
    const outcome = await host.open(tempId, { cancelMark: mark, origin: { path: p.name, label: p.displayName ?? p.name } });
    if (outcome === 'cancelled') return;
    onPick(multi ? [...already, tempId] : [tempId]);
  };
  const title = `Pick ${targets.length === 1 ? (targets[0]!.displayName ?? targets[0]!.name) : (p.displayName ?? p.name)}`;
  return (
    <Modal
      title={title}
      open
      onClose={onClose}
      size="l"
      className="picker"
      footer={
        <>
          {allowNone && (
            <Button variant="ghost" onClick={() => onPick([])}>
              None
            </Button>
          )}
          {multi && (
            <Button variant="ghost" onClick={() => setChosen(new Set(rows.map((r) => r.id)))}>
              Select all
            </Button>
          )}
          <span className="spacer" />
          <Button onClick={onClose}>Cancel</Button>
          {multi && (
            <Button variant="primary" onClick={() => onPick(Array.from(chosen))}>
              Select {chosen.size > 0 ? `(${chosen.size})` : ''}
            </Button>
          )}
        </>
      }
    >
      <div className="picker-head">
        {targets.length > 1 && (
          <Select size="s" value={template} onChange={(e) => setTemplate(e.target.value)} aria-label="Template">
            {targets.map((t) => (
              <option key={t.name} value={t.name}>
                {t.displayName ?? t.name}
                {t.name === (p.template ?? '') ? ' (and heirs)' : ''}
              </option>
            ))}
          </Select>
        )}
        <form
          className="row"
          onSubmit={(e) => {
            e.preventDefault();
            searchBox.applyNow();
          }}
        >
          <TextInput size="s" value={searchBox.text} onChange={(e) => searchBox.setText(e.target.value)} placeholder="Search…" aria-label="Search documents" />
        </form>
        <span className="spacer" />
        {targets.length > 1 && (
          <Select size="s" value={createIn} onChange={(e) => setCreateIn(e.target.value)} aria-label="Create in template">
            {targets.map((t) => (
              <option key={t.name} value={t.name}>
                {t.displayName ?? t.name}
              </option>
            ))}
          </Select>
        )}
        <Button size="s" onClick={() => void create()} disabled={!createIn} icon={<Plus aria-hidden="true" />}>
          Create
        </Button>
      </div>
      {error && (
        <div className="banner banner-danger" role="alert">
          <AlertCircle className="banner-icon" aria-hidden="true" />
          <div className="banner-body">{error}</div>
        </div>
      )}
      {rows.length === 0 && loading ? (
        <div className="skeleton-rows" role="status" aria-label="Loading">
          {[0, 1, 2].map((i) => (
            <div key={i} className="skeleton-row">
              <span className="skeleton" />
              <span className="skeleton" />
            </div>
          ))}
        </div>
      ) : null}
      {rows.length === 0 && !loading && <EmptyState icon={<FileText />} title={search ? 'No document matches' : 'No document of this template yet'} body={search ? 'Change the search, or create a new one.' : 'Create makes the first one and opens it in the editor.'} />}
      <div className="picker-cards" role={multi ? 'group' : 'listbox'} aria-label={title}>
        {rows.map((d) => (
          <DocumentCard key={d.id} doc={d} selected={chosen.has(d.id)} multi={!!multi} onClick={() => toggle(d.id)} template={catalog.templates.get(d.template)} />
        ))}
        {cursor && (
          <button type="button" className="slot-card add" onClick={() => void load(cursor)} disabled={loading} aria-busy={loading || undefined}>
            {loading ? <span className="spinner" aria-hidden="true" /> : null}
            Load more
          </button>
        )}
      </div>
    </Modal>
  );
}

/** `[id] label` with the first three values and a thumbnail (the first image asset of the document). */
export function DocumentCard({ doc, template, selected, multi, onClick }: { doc: DocumentLike; template?: Template; selected: boolean; multi: boolean; onClick: () => void }) {
  const { catalog } = useWidgetEnv();
  const params = template?.effectiveParameters ?? [];
  const data = (doc.data ?? {}) as Record<string, Json>;
  const image = params.find((p) => p.type === 'asset' && typeof data[p.name] === 'string' && catalog.assets.get(data[p.name] as string)?.kind === 'image');
  const asset = image ? catalog.assets.get(data[image.name] as string) : undefined;
  const { client, org, game, branch } = useWidgetEnv();
  const thumb = useAssetUrls(asset?.contentPath ? { client, org, game, branch } : null, asset ? [asset.id] : [])[asset?.id ?? ''];
  const firstThree = params.filter((p) => !p.usedInName && data[p.name] !== null && data[p.name] !== undefined && p !== image).slice(0, 3);
  return (
    <button type="button" className={`doc-card ${selected ? 'selected' : ''}`} onClick={onClick} role={multi ? 'checkbox' : 'option'} aria-checked={multi ? selected : undefined} aria-selected={multi ? undefined : selected} data-testid={`card-${doc.id}`}>
      {asset?.contentPath && thumb && <img className="doc-thumb" src={thumb} alt="" />}
      <span className="doc-card-body">
        <span className="doc-card-title">
          <code className="muted small">[{shortId(doc)}]</code> {catalog.label(doc)}
        </span>
        {template && template.name !== doc.template && <Badge>{doc.template.split('.').pop()}</Badge>}
        <span className="doc-card-values small muted">
          {firstThree.map((p) => (
            <span key={p.name}>
              {p.displayName ?? p.name}: {formatValue(p, data[p.name], catalog.format)}
            </span>
          ))}
        </span>
      </span>
    </button>
  );
}
